import { useState } from 'react';
import Tooltip from '@/components/Tooltip';
import { InfluencerStatusFlowGuide } from '@/components/InfluencerStatusFlowDialog';

// Hidden in "Show simply" mode when their count is 0 (unless selected) —
// visibility behavior only, kept separate from badge coloring below.
const MUTED_STATUSES = new Set(['rejected', 'deleted']);

// Terminal-outcome statuses get a colored icon so they read as a distinct
// "final result" group, separated from the in-progress flow above by a divider
// (see `campaign-status-item--divider` below). Colors reuse the GGC status tokens.
const OUTCOME_ICON_CLASS: Record<string, string> = {
  completed: 'campaign-status-icon--done',
  incomplete: 'campaign-status-icon--hold',
  rejected: 'campaign-status-icon--exit',
  deleted: 'campaign-status-icon--exit',
};

// Count-badge coloring for the terminal-outcome rows: Completed gets a green
// badge matching its icon (campaign-status-icon--done); Incomplete/Rejected/
// Deleted share the same dark-gray "muted" badge instead of the default blue,
// so all three non-success outcomes read as one visual group.
const DONE_BADGE_STATUSES = new Set(['completed']);
const DARK_BADGE_STATUSES = new Set(['incomplete', 'rejected', 'deleted']);

// Active-fulfillment statuses (content already secured, payment underway) get the
// cta-blue icon color to stand out from the earlier candidate-sourcing steps above.
const ACTIVE_ICON_CLASS: Record<string, string> = {
  content_creation: 'campaign-status-icon--active',
  payment: 'campaign-status-icon--active',
  payment_done: 'campaign-status-icon--active',
};

export function CampaignStatusFilter({ statuses, influencerStatuses, selectedStatuses, onStatusClick, t }: {
  statuses: { value: string; text: string; textKo?: string; icon: string }[];
  influencerStatuses: { byIdTotal?: Record<string, number>; byIdUnread?: Record<string, number> };
  selectedStatuses: string[];
  onStatusClick: (status: string) => void;
  t: (en: string, ko: string) => string;
}) {
  const [showAll, setShowAll] = useState(true);
  const [open, setOpen] = useState(true);

  const totalCount = statuses.reduce((sum, s) => sum + (influencerStatuses.byIdTotal?.[s.value] ?? 0), 0);

  const visibleStatuses = showAll
    ? statuses
    : statuses.filter((s) => {
        const total = influencerStatuses.byIdTotal?.[s.value] ?? 0;
        if (MUTED_STATUSES.has(s.value)) return selectedStatuses.includes(s.value);
        return total > 0 || selectedStatuses.includes(s.value);
      });

  const hiddenCount = statuses.length - visibleStatuses.length;

  return (
    <div className="filter-section campaign-status-filter">
      <div className="campaign-stats-toggle">
        <i className="fa fa-users campaign-stats-toggle-icon" />
        <span className='text-[11px] tracking-[-0.4px] font-medium flex items-center gap-0.2'>
          {t('Influencer status', '인플루언서 상태')}
          <InfluencerStatusFlowGuide variant="icon" />
        </span>
        {totalCount > 0 && <span className="campaign-stats-toggle-count">{totalCount.toLocaleString()}</span>}
        <Tooltip content={t('Click to expand or collapse', '클릭하면 펼치기/접기가 가능합니다')}>
          <button type="button" className="campaign-stats-toggle-caret-btn" onClick={() => setOpen((v) => !v)}>
            <i className={`fa fa-caret-${open ? 'up' : 'down'}`} />
          </button>
        </Tooltip>
      </div>
      {open && (
        <div className="campaign-stats-body campaign-status-list">
          {visibleStatuses.map((status, i) => {
            const isSelected = selectedStatuses.includes(status.value);
            const total = influencerStatuses.byIdTotal?.[status.value] ?? 0;
            const unread = influencerStatuses.byIdUnread?.[status.value] ?? 0;
            const hasCount = total > 0;
            const isDoneBadge = DONE_BADGE_STATUSES.has(status.value);
            const isDarkBadge = DARK_BADGE_STATUSES.has(status.value);
            const outcomeIconClass = OUTCOME_ICON_CLASS[status.value];
            const iconClass = outcomeIconClass ?? ACTIVE_ICON_CLASS[status.value];
            // Divider sits above the first outcome row that directly follows a
            // non-outcome row — stays correct even in "Show simply" mode, where
            // some in-progress statuses above it may be hidden.
            const isDividerTop = !!outcomeIconClass && !OUTCOME_ICON_CLASS[visibleStatuses[i - 1]?.value ?? ''];
            return (
              <div
                key={status.value}
                className={`campaign-status-item${isSelected ? ' campaign-status-item--selected' : ''}${isDividerTop ? ' campaign-status-item--divider' : ''}`}
                onClick={() => onStatusClick(status.value)}
              >
                <i className={`fa fa-${status.icon} w-[16px] text-center mr-1.5${iconClass ? ` ${iconClass}` : ''}`} />
                <span className="campaign-status-text">
                  {/* Influencer status names are shown in English only, regardless of app language. */}
                  {status.text}
                </span>
                <span className="campaign-status-counts">
                  <Tooltip content={t('Influencers in this status', '해당 상태의 인플루언서')}>
                    <span className={`label ${!hasCount ? 'label-gray' : isDoneBadge ? 'label-done' : isDarkBadge ? 'label-muted' : 'label-info'}`}>{total}</span>
                  </Tooltip>
                  {unread > 0 && <Tooltip content={t('Unread conversations', '읽지 않은 대화')}><span className="label label-primary">{unread}</span></Tooltip>}
                </span>
              </div>
            );
          })}
          {!showAll && hiddenCount > 0 && (
            <button type="button" className="campaign-status-show-all" onClick={() => setShowAll(true)}>
              {t('Show all', '모두 보기')} (+{hiddenCount})
            </button>
          )}
          {showAll && (
            <button type="button" className="campaign-status-show-all" onClick={() => setShowAll(false)}>
              {t('Show simply', '간략히 보기')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
